import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Typography, TextField, List, ListItem, ListItemText, IconButton, Divider } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Searchable from "../../../../layout/searchable/Searchable";
import { X as XMark } from "lucide-react";
import { ReactComponent as InfoIcon } from "../../../../../assets/icons/svg/info.svg";
import {
  changeAddModalView,
  addOnHandAmount,
  editOnHandAmount,
  removeOnHandAmount,
  reorderOnHandAmounts,
} from "../../../../../actions/pantry";
import { formatAmountUnit, formatIngredient, formatState } from "../../../../../utils/recipe";

const ItemIngredients = ({
  pantry,
  changeAddModalView,
  addOnHandAmount,
  editOnHandAmount,
  removeOnHandAmount,
  reorderOnHandAmounts,
}) => {
  // Modes: 'list' (view all), 'edit' (create/edit one)
  const [mode, setMode] = useState("list");
  const [editIndex, setEditIndex] = useState(null); // null = create, number = edit

  // Form state
  const [ingredient, setIngredient] = useState([]);
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState([]);
  const [state, setState] = useState("");
  const [note, setNote] = useState("");

  // Error states
  const [amountError, setAmountError] = useState("");
  const [ingredientError, setIngredientError] = useState("");
  const [unitError, setUnitError] = useState("");

  // --- Validation Functions ---
  const verifyAmount = (value) => {
    let error = "";
    const fractionRegex =
      /^([0-9]{1,3}\/[0-9]{1,3})$|^([0-9]{1,3}\/)$/ ||
      /^([0-9]{1,3}\s+[0-9]{1,3}\/[0-9]{1,3})$|^([0-9]{1,3})$|^([0-9]{1,3}\.[0-9]+)$/;
    if (!value || value === "") {
      error = "Amount is required";
      return error;
    }
    // Allow fractions or decimals
    if (typeof value === "string") {
      // Handle compound fraction (e.g., "1 1/2")
      if (value.includes(" ")) {
        const [whole, fraction] = value.split(" ");
        const [numerator, denominator] = fraction.split("/");
        const total = parseInt(whole) + parseInt(numerator) / parseInt(denominator);
        if (total > 10000) error = "Amount must be ≤ 10,000";
        else if (total < 0.001) error = "Amount must be ≥ 0.001";
      } else if (value.includes("/")) {
        const [numerator, denominator] = value.split("/");
        if (!denominator) error = "Invalid fraction";
        else {
          const total = parseInt(numerator) / parseInt(denominator);
          if (total > 10000) error = "Amount must be ≤ 10,000";
          else if (total < 0.001) error = "Amount must be ≥ 0.001";
        }
      } else if (!isNaN(value)) {
        const num = parseFloat(value);
        if (num > 10000) error = "Amount must be ≤ 10,000";
        else if (num < 0.001) error = "Amount must be ≥ 0.001";
      } else {
        error = "Amount must be a number or fraction";
      }
    }
    return error;
  };

  const verifyIngredient = (item) => {
    let error = "";
    if (!item || !item.name) error = "Ingredient is required";
    else if (item.name.length > 64) error = "Name too long";
    return error;
  };

  const verifyUnit = (item) => {
    let error = "";
    if (item && item.name && item.name.length > 32) error = "Unit too long";
    return error;
  };

  // --- List View ---
  const amounts = pantry.create?.data?.onHand?.amounts || [];

  // Start in edit mode if empty, else list mode
  useEffect(() => {
    if (amounts.length === 0 && mode !== "edit") {
      setMode("edit");
      setEditIndex(null);
    }
    // If an item was just added (mode was 'edit', now amounts has 1), go to list
    if (mode === "edit" && amounts.length > 0 && editIndex === null) {
      setMode("list");
      setEditIndex(null);
    }
  }, [amounts.length]);

  useEffect(() => {
    if (amounts.length === 0) {
      setMode("edit");
      setEditIndex(null);
    }
  }, []);

  const handleListEdit = (idx) => {
    const item = amounts[idx];
    setIngredient(item.ingredient || []);
    setAmount(item.amount || "");
    setUnit(item.unit || []);
    setState(item.state || "");
    setNote(item.note || "");
    setEditIndex(idx);
    setMode("edit");
  };

  const handleListAdd = () => {
    setIngredient([]);
    setAmount("");
    setUnit([]);
    setState("");
    setNote("");
    setEditIndex(null);
    setMode("edit");
  };

  const handleListRemove = (idx) => {
    removeOnHandAmount(idx);
  };

  const handleListBack = () => {
    changeAddModalView({ stage: "location" });
  };

  const handleListNext = () => {
    changeAddModalView({ stage: "expiry" });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    reorderOnHandAmounts(result.source.index, result.destination.index);
  };

  // --- Edit/Create Mode ---
  const handleFormSave = () => {
    const amountErr = verifyAmount(amount);
    const ingredientErr = verifyIngredient(ingredient);
    const unitErr = verifyUnit(unit);
    setAmountError(amountErr);
    setIngredientError(ingredientErr);
    setUnitError(unitErr);
    if (amountErr || ingredientErr || unitErr) return;
    const alias = {
      ingredient,
      amount,
      unit,
      state,
      note,
    };
    if (editIndex === null) {
      addOnHandAmount(alias);
    } else {
      editOnHandAmount(editIndex, alias);
    }
    setMode("list");
    setEditIndex(null);
  };

  const handleFormCancel = () => {
    setMode("list");
    setEditIndex(null);
  };

  // Replace showState/showNote with one toggle
  const [showOptionalFields, setShowOptionalFields] = useState(!!state || !!note);

  // When hiding, clear both
  const handleHideOptionalFields = () => {
    setState("");
    setNote("");
    setShowOptionalFields(false);
  };

  // Always show if either has a value
  useEffect(() => {
    if ((state || note) && !showOptionalFields) setShowOptionalFields(true);
  }, [state, note]);

  // --- Render ---
  if (mode === "list") {
    return (
      <div className="w-full">
        <div className="p-4 bg-neutral-100 w-full">
          <Typography variant="h6" className="font-normal">
            Ingredient Amounts
          </Typography>
        </div>

        <div className="p-4">
          <div className="pb-2 flex flex-row justify-between w-full">
            <Button size="small" className="text-primaryText px-0 hover:bg-transparent" disableRipple>
              Quantity: 1x
            </Button>
            <Button onClick={handleListAdd} disableRipple size="small" className="px-0 hover:bg-transparent">
              <InfoIcon className="h-3 w-auto fill-primaryText mr-1" /> Help
            </Button>
          </div>
          <div className="border border-neutral-300 rounded-lg bg-white max-h-60vh overflow-y-auto">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="ingredient-list">
                {(provided) => (
                  <List ref={provided.innerRef} {...provided.droppableProps} className="p-0">
                    {amounts.map((item, idx) => (
                      <Draggable key={idx} draggableId={String(idx)} index={idx}>
                        {(provided, snapshot) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              left: "auto !important",
                              top: "auto !important",
                            }}
                            button
                            onClick={() => handleListEdit(idx)}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleListRemove(idx);
                                }}
                              >
                                <XMark size={20} />
                              </IconButton>
                            }
                            className={`rounded-md shadow-sm border border-neutral-200 ${
                              snapshot.isDragging ? "bg-neutral-100" : "bg-white"
                            }`}
                          >
                            <ListItemText
                              primary={(() => {
                                const displayIngredient = {
                                  ...item.ingredient,
                                  amount: item.amount,
                                  unit: item.unit?.display
                                    ? item.unit
                                    : {
                                        display: {
                                          magnitude: item.amount,
                                          text: item.unit?.text || item.unit?.name || "",
                                        },
                                      },
                                  state: item.state,
                                };
                                return (
                                  <>
                                    <span className="font-normal">{formatAmountUnit(displayIngredient)}</span>{" "}
                                    <span className="font-medium">{formatIngredient(displayIngredient)}</span>
                                    {item.state && item.state !== "" && (
                                      <span className="font-normal italic text-secondaryText text-sm">
                                        {formatState(displayIngredient)}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                              secondary={
                                item.note && item.note !== "" ? (
                                  <span className="text-secondaryText">({item.note})</span>
                                ) : null
                              }
                            />
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div className="pt-4 w-full flex flex-row justify-between">
            <Button
              variant="contained"
              color="secondary"
              className="text-primaryText"
              onClick={handleListAdd}
              disableRipple
              disableElevation
              fullWidth
              size="small"
            >
              + Add Ingredient Option
            </Button>
          </div>
        </div>
        <div className="px-4 pb-4 flex justify-between gap-2">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleListBack}
            disableRipple
            disableElevation
            className="text-primaryText"
          >
            Back
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleListNext}
            disabled={amounts.length === 0}
            disableRipple
            disableElevation
          >
            Next
          </Button>
        </div>
      </div>
    );
  }

  // Edit/Create Mode
  return (
    <div className="w-full">
      <div className="p-4 bg-neutral-100">
        <Typography variant="h6" className="font-normal">
          Ingredient Amounts
        </Typography>
      </div>
      <div className="p-4 flex flex-col gap-4">
        <div className="flex gap-2 w-full">
          <div className="w-full">
            <TextField
              label="Amount"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountError(verifyAmount(e.target.value));
              }}
              type="text"
              inputProps={{ min: 0, step: "any" }}
              className="w-full"
              error={!!amountError}
              helperText={amountError}
            />
          </div>
          <div className="w-full">
            <Searchable
              label="Unit"
              multiple={false}
              apiUrl="/units/searchable"
              onItemChange={(item) => {
                setUnit(item);
                setUnitError(verifyUnit(item));
              }}
              selectedItemsFromParent={unit}
              error={!!unitError}
              helperText={unitError}
              className="w-full"
            />
          </div>
        </div>
        <Searchable
          label="Ingredient"
          multiple={false}
          apiUrl="/ingredients/searchable"
          onItemChange={(item) => {
            setIngredient(item);
            setIngredientError(verifyIngredient(item));
          }}
          selectedItemsFromParent={ingredient}
          error={!!ingredientError}
          helperText={ingredientError}
        />
        <div>
          {showOptionalFields ? (
            <>
              <Button onClick={handleHideOptionalFields} disableRipple className="hover:bg-transparent justify-start">
                <Typography variant="subtitle2" className="text-secondaryText-600 font-normal text-xs">
                  - Hide Notes/Ingredient State
                </Typography>
              </Button>
              <div className="mt-2 flex flex-col w-full gap-4">
                <TextField
                  label="State"
                  variant="outlined"
                  fullWidth
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  inputProps={{ maxLength: 32 }}
                />
                <TextField
                  label="Notes"
                  variant="outlined"
                  fullWidth
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  inputProps={{ maxLength: 128 }}
                />
              </div>
            </>
          ) : (
            <Button
              onClick={() => setShowOptionalFields(true)}
              disableRipple
              className="hover:bg-transparent justify-start"
            >
              <Typography variant="subtitle2" className="text-secondaryText-600 font-normal text-xs">
                + Add Notes/Ingredient State
              </Typography>
            </Button>
          )}
        </div>

        <div className="flex justify-between mt-4 gap-2">
          <Button
            variant="contained"
            color="secondary"
            className="text-primaryText"
            onClick={handleFormCancel}
            disableRipple
            disableElevation
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleFormSave} disableRipple disableElevation>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

ItemIngredients.propTypes = {
  pantry: PropTypes.object.isRequired,
  changeAddModalView: PropTypes.func.isRequired,
  addOnHandAmount: PropTypes.func.isRequired,
  editOnHandAmount: PropTypes.func.isRequired,
  removeOnHandAmount: PropTypes.func.isRequired,
  reorderOnHandAmounts: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  pantry: state.pantry,
});

export default connect(mapStateToProps, {
  changeAddModalView,
  addOnHandAmount,
  editOnHandAmount,
  removeOnHandAmount,
  reorderOnHandAmounts,
})(ItemIngredients);
